/** Fixed product catalog and state snapshot for the Weather Sicily map. */

const TEMPORAL_MODES = new Set(['live', 'nowcast', 'forecast', 'history', 'all']);

function deepFreeze(value) {
    if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
    for (const nested of Object.values(value)) deepFreeze(nested);
    return Object.freeze(value);
}

export const PLATFORM_SECTIONS = deepFreeze({
    LIVE: {
        id: 'live',
        label: 'Live',
        order: 10,
        description: 'Osservazioni, radar, satellite e fenomeni in corso.',
    },
    FORECAST: {
        id: 'forecast',
        label: 'Forecast',
        order: 20,
        description: 'Nowcast, modelli, previsioni locali e fisica futura.',
    },
    TRACKING: {
        id: 'tracking',
        label: 'Tracking',
        order: 30,
        description: 'Evoluzione temporale di sistemi ed eventi territoriali.',
    },
    SYSTEM: {
        id: 'system',
        label: 'Sistema',
        order: 40,
        description: 'Allerte, qualità, stato locale e salute operativa.',
    },
});

const SECTION_BY_ID = new Map(
    Object.values(PLATFORM_SECTIONS).map(section => [section.id, section]),
);

function finiteNumber(value, fallback = null) {
    if (typeof value !== 'number' && typeof value !== 'string') return fallback;
    if (typeof value === 'string' && value.trim() === '') return fallback;
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
}

function count(value) {
    if (Array.isArray(value)) return value.length;
    if (value && typeof value === 'object') {
        for (const key of ['count', 'event_count', 'observation_count', 'fresh_count', 'length']) {
            const candidate = finiteNumber(value[key]);
            if (candidate !== null) return candidate;
        }
    }
    return 0;
}

function result(status, metrics = {}, options = {}) {
    const available = options.available ?? !['unavailable', 'error'].includes(status);
    return {
        status,
        available: Boolean(available),
        reason: options.reason ?? null,
        metrics: metrics && typeof metrics === 'object' ? metrics : {},
        coverage: options.coverage ?? null,
        maturity: options.maturity,
        authority: options.authority,
        decisionEligible: options.decisionEligible,
    };
}

function unavailable(reason = 'state_not_published') {
    return result('unavailable', {}, {available: false, reason});
}

function liveFusionStatus(state) {
    const fusion = state?.live?.total_fusion;
    if (!fusion) return unavailable('live_fusion_not_published');
    const sourceCount = finiteNumber(fusion.source_count, 0);
    const freshSourceCount = finiteNumber(fusion.fresh_source_count, 0);
    const status = sourceCount > 0 && freshSourceCount >= sourceCount ? 'operational'
        : sourceCount > 0 ? 'degraded' : 'unavailable';
    return result(status, {
        sourceCount,
        freshSourceCount,
        insightCount: count(fusion.insights),
        categoryCount: fusion.categories && typeof fusion.categories === 'object'
            ? Object.keys(fusion.categories).length : 0,
    }, {available: sourceCount > 0});
}

function stationStatus(state) {
    const stations = state?.live?.stations;
    if (!stations) return unavailable('station_summary_not_published');
    const freshCount = finiteNumber(stations.fresh_count, 0);
    const quality = state?.live?.station_quality || {};
    return result(freshCount > 0 ? 'operational' : 'degraded', {
        freshCount,
        rainingCount: count(stations.raining),
        strongGustCount: count(stations.strong_gusts),
        disabledMetricCount: finiteNumber(
            quality.disabled_metrics_total ?? quality.auto_disabled_metrics,
            0,
        ),
    });
}

function radarStatus(state) {
    const radar = state?.live?.wsnow_rain_field;
    if (!radar) return unavailable('radar_summary_not_published');
    const frames = Array.isArray(radar.frames) ? radar.frames : [];
    const maxLeadMinutes = frames.reduce(
        (maximum, frame) => Math.max(maximum, finiteNumber(frame?.lead_min, 0)),
        0,
    );
    return result(radar.available === false ? 'unavailable' : 'operational', {
        frameCount: frames.length,
        maxLeadMinutes,
        baseTimeUtc: radar.base_time_utc ?? null,
        engine: radar.engine ?? null,
    }, {
        available: radar.available !== false,
        coverage: {maxLeadMinutes},
    });
}

function satelliteStatus(state) {
    const satellite = state?.satellite;
    if (!satellite?.frame_time_utc) return unavailable('satellite_frame_not_published');
    const lagMinutes = finiteNumber(satellite.time_lag_min);
    return result(lagMinutes === null || lagMinutes > 120 ? 'degraded' : 'operational', {
        frameTimeUtc: satellite.frame_time_utc,
        lagMinutes,
        agreementPct: finiteNumber(satellite.agreement_pct),
        cloudPct: finiteNumber(satellite.sat_cloud_pct),
    });
}

function lightningStatus(state) {
    const lightning = state?.live?.lightning;
    if (!lightning) return unavailable('lightning_summary_not_published');
    return result(lightning.heartbeat_at_utc || lightning.latest_at_utc ? 'operational' : 'degraded', {
        count1h: finiteNumber(lightning.count_1h, 0),
        latestAtUtc: lightning.latest_at_utc ?? null,
        heartbeatAtUtc: lightning.heartbeat_at_utc ?? null,
    });
}

function stormStatus(state) {
    const storms = state?.live?.storm_cells;
    if (!Array.isArray(storms)) return unavailable('storm_tracking_not_published');
    return result('operational', {activeCellCount: storms.length});
}

function hailStatus(state) {
    const radar = radarStatus(state);
    if (!radar.available) return radar;
    return result(radar.status, {
        radarFrameCount: radar.metrics.frameCount,
        baseTimeUtc: radar.metrics.baseTimeUtc,
    });
}

function phenomenonStatus(state) {
    const phenomena = state?.live?.phenomena;
    if (!phenomena) return unavailable('live_phenomena_not_published');
    return result('operational', {
        eventCount: count(phenomena.events),
        stationCount: finiteNumber(phenomena.station_count, 0),
        generatedAtUtc: phenomena.generated_at ?? null,
        kinds: phenomena.counts && typeof phenomena.counts === 'object'
            ? {...phenomena.counts} : {},
    });
}

function nowcastStatus(state) {
    const radar = radarStatus(state);
    if (!radar.available) return unavailable('wsnow_not_published');
    const maxLeadMinutes = finiteNumber(radar.metrics?.maxLeadMinutes, 0);
    const operational = radar.status === 'operational' && maxLeadMinutes > 0;
    return result(operational ? 'operational' : 'degraded', {
        frameCount: finiteNumber(radar.metrics?.frameCount, 0),
        maxLeadMinutes,
        baseTimeUtc: radar.metrics?.baseTimeUtc ?? null,
        engine: radar.metrics?.engine ?? null,
    }, {
        available: operational,
        coverage: {maxLeadMinutes},
        decisionEligible: operational,
        authority: 'WS Now',
    });
}

function localForecastStatus(state) {
    const engine = state?.weather_state_engine;
    const v2 = state?.weather_state_v2;
    const localityCount = finiteNumber(engine?.locality_count ?? v2?.locality_count, 0);
    if (!engine && !v2) return unavailable('local_forecast_not_published');
    const served = engine?.served_forecast_horizon_hours;
    const servedHours = finiteNumber(served?.max ?? served?.min ?? engine?.horizon_hours);
    return result(localityCount > 0 ? 'operational' : 'degraded', {
        localityCount,
        observedCoverageCount: finiteNumber(v2?.observed_coverage_count, 0),
        servedHorizonHours: servedHours,
        targetHorizonHours: finiteNumber(served?.target ?? engine?.horizon_hours),
    }, {coverage: {maxLeadHours: servedHours}});
}

function modelFieldStatus(state) {
    const model = state?.model;
    const sources = state?.model_sources;
    if (!model && !sources) return unavailable('model_fields_not_published');
    const entries = sources && typeof sources === 'object'
        ? Object.entries(sources).filter(([name]) => name !== 'consensus') : [];
    const availableModels = entries.filter(([, source]) => source?.status === 'available');
    return result(availableModels.length || model ? 'operational' : 'degraded', {
        primaryModel: model?.name ?? null,
        runUtc: model?.run_utc ?? null,
        validNowUtc: model?.valid_now_utc ?? null,
        timestepCount: count(model?.steps_h),
        modelCount: availableModels.length,
        models: availableModels.map(([name]) => name),
    });
}

function physicsStatus(state) {
    const physics = state?.atmospheric_physics;
    if (!physics) return unavailable('atmospheric_physics_not_published');
    const degraded = physics.degraded === true || physics.status === 'degraded';
    return result(degraded ? 'degraded' : 'operational', {
        timestepCount: count(physics.timesteps),
        horizonHours: finiteNumber(physics.horizon_hours),
        profilePointCount: count(physics.profile_points),
        algorithmVersion: physics.algorithm_version ?? null,
    }, {coverage: {maxLeadHours: finiteNumber(physics.horizon_hours)}});
}

function synopticStatus(state) {
    const scenario = state?.synoptic_scenario;
    if (!scenario) return unavailable('synoptic_scenario_not_published');
    const timesteps = Array.isArray(scenario.timesteps) ? scenario.timesteps : [];
    const nowIndex = finiteNumber(scenario.now_index);
    const current = Number.isInteger(nowIndex) ? timesteps[nowIndex] : null;
    const currentCenters = current?.features ?? current?.centers;
    const horizonHours = finiteNumber(scenario.forecast_horizon_h);
    return result(scenario.status === 'available' && timesteps.length ? 'operational' : 'degraded', {
        timestepCount: timesteps.length,
        trackCount: count(scenario.tracks),
        currentCenterCount: count(currentCenters),
        nowIndex,
        forecastHorizonHours: horizonHours,
    }, {
        available: timesteps.length > 0,
        coverage: {maxLeadHours: horizonHours},
    });
}

function frontStatus(state) {
    const advanced = state?.advanced_synoptic;
    if (!advanced) return unavailable('advanced_synoptic_not_published');
    const timesteps = Array.isArray(advanced.timesteps) ? advanced.timesteps : [];
    return result(timesteps.length ? 'operational' : 'degraded', {
        timestepCount: timesteps.length,
        currentFile: advanced.current?.file ?? advanced.current ?? null,
        indexFile: advanced.index_file ?? null,
    }, {available: timesteps.length > 0});
}

function territorialStatus(state) {
    const territorial = state?.territorial_intelligence;
    if (!territorial) return unavailable('territorial_intelligence_not_published');
    const eventCount = finiteNumber(territorial.event_count, count(territorial.events));
    const horizonHours = finiteNumber(territorial.horizon_hours);
    return result('operational', {
        eventCount,
        horizonHours,
        briefingIt: territorial.briefing_it ?? null,
    }, {coverage: {maxLeadHours: horizonHours}});
}

function weatherStateStatus(state) {
    const weather = state?.weather_state_v2 ?? state?.weather_state_engine;
    if (!weather) return unavailable('weather_state_not_published');
    const localityCount = finiteNumber(weather.locality_count, 0);
    return result(localityCount > 0 ? 'operational' : 'degraded', {
        localityCount,
        observedCoverageCount: finiteNumber(weather.observed_coverage_count, 0),
        generatedAtUtc: weather.generated_at_utc ?? null,
        schemaVersion: weather.schema_version ?? null,
    });
}

function alertStatus(state) {
    const policy = state?.notification_policy;
    if (!policy) return unavailable('notification_policy_not_published');
    const operational = policy.status === 'operational'
        && finiteNumber(policy.blocking_bypass_count, 0) === 0;
    return result(operational ? 'operational' : 'degraded', {
        policyVersion: policy.policy_version ?? null,
        authorityState: policy.authority_state ?? null,
        blockingBypassCount: finiteNumber(policy.blocking_bypass_count, 0),
        stuckCandidateCount: finiteNumber(policy.stuck_candidate_count, 0),
        gatedPushCount24h: finiteNumber(policy.intelligence_gated_pushes_24h, 0),
    }, {
        decisionEligible: operational,
        authority: policy.authority ?? 'Weather Sicily Intelligence',
    });
}

function geophysicalStatus(state) {
    const geophysical = state?.geophysical_intelligence;
    if (!geophysical) return unavailable('geophysical_intelligence_not_published');
    return result(geophysical.status === 'operational' ? 'operational' : 'degraded', {
        eventCount: count(geophysical.events),
        generatedAtUtc: geophysical.generated_at_utc ?? null,
        etnaState: geophysical.etna?.state ?? null,
        freshCameraCount: finiteNumber(geophysical.volcanic_imagery?.fresh_camera_count, 0),
        earthquakeCount24h: finiteNumber(geophysical.seismicity?.count_24h, 0),
    });
}

function stationQualityStatus(state) {
    const quality = state?.live?.station_quality;
    if (!quality) return unavailable('station_quality_not_published');
    return result('operational', {
        disabledMetricCount: finiteNumber(
            quality.disabled_metrics_total ?? quality.auto_disabled_metrics,
            0,
        ),
        autoDisabledMetricCount: finiteNumber(quality.auto_disabled_metrics, 0),
        metricCount: count(quality.metrics),
    }, {decisionEligible: false, authority: 'sensor_quality_control'});
}
function sourceHealthStatus(state, sourceId, options = {}) {
    const sources = Array.isArray(state?.source_health) ? state.source_health
        : Array.isArray(state?.live?.source_health) ? state.live.source_health : [];
    const source = sources.find(item => item?.id === sourceId);
    if (!source) return unavailable(sourceId + '_source_health_not_published');
    const status = source.status === 'fresh' ? 'operational' : 'degraded';
    return result(status, {
        sourceId,
        sourceLabel: source.label ?? sourceId,
        ageMinutes: finiteNumber(source.age_min),
        maxAgeMinutes: finiteNumber(source.max_age_min),
        ...(options.metrics || {}),
    }, {
        available: source.status === 'fresh',
        reason: source.status === 'fresh' ? null : 'source_not_fresh',
        decisionEligible: options.decisionEligible,
        authority: options.authority,
    });
}

function compositionStatus(state) {
    const composition = state?.atmospheric_composition;
    if (!composition) return unavailable('atmospheric_composition_not_published');
    const available = !['unavailable', 'error'].includes(String(composition.status || ''));
    const episode = composition.episode || {};
    return result(available ? 'operational' : 'unavailable', {
        compositionStatus: composition.status ?? null,
        eventCount: count(composition.events),
        timelineStepCount: count(composition.timeline),
        horizonHours: finiteNumber(composition.horizon_hours),
        peakDustUgM3: finiteNumber(episode.peak_dust_ug_m3),
        peakAod: finiteNumber(episode.peak_aod),
        peakDay: episode.peak_day ?? null,
    }, {
        available,
        coverage: {maxLeadHours: finiteNumber(composition.horizon_hours)},
        authority: 'copernicus_cams_model_context',
        decisionEligible: false,
    });
}

function earthquakeStatus(state) {
    const seismicity = state?.geophysical_intelligence?.seismicity;
    if (!seismicity) return unavailable('seismicity_not_published');
    return result('operational', {
        count24h: finiteNumber(seismicity.count_24h, count(seismicity.earthquakes_24h)),
        count7d: finiteNumber(seismicity.count_7d, count(seismicity.earthquakes_7d)),
        maxMagnitude7d: finiteNumber(seismicity.max_magnitude_7d),
        crowdReportCount24h: count(seismicity.crowd_events_24h),
        deterministicForecastAllowed: false,
    }, {
        authority: 'ingv_official_observation',
        decisionEligible: false,
    });
}

function fireObservationStatus(state) {
    return sourceHealthStatus(state, 'wildfire', {
        decisionEligible: false,
        authority: 'nasa_firms_observed_hotspots',
        metrics: {
            fireEventCount: finiteNumber(state?.live?.total_fusion?.categories?.fire, 0),
        },
    });
}


const action = (type, target, value) => ({type, target, value});

const BUILTIN_DEFINITIONS = [
    {
        id: 'ws.live.intelligence-fusion', section: 'live', order: 10,
        title: 'WS Live Fusion', description: 'Sintesi multi-sorgente del presente.',
        temporalPolicy: 'live', maturity: 'operational', authority: 'ws_intelligence', decisionEligible: true,
        actions: [action('section', 'wsi-panel', 'summary'), action('timeline', 'unified', 'live')],
        tags: ['live', 'fusion', 'overview'], derive: liveFusionStatus,
    },
    {
        id: 'ws.live.stations', section: 'live', order: 20,
        title: 'Stazioni Weather Sicily', description: 'Reti osservative e parametri misurati.',
        temporalPolicy: 'live', maturity: 'operational', authority: 'observed', decisionEligible: true,
        actions: [action('control', 'chkMarkers', true), action('section', 'view-options', 'stations')],
        tags: ['stations', 'observations'], derive: stationStatus,
    },
    {
        id: 'ws.live.radar', section: 'live', order: 30,
        title: 'Radar pioggia', description: 'Osservazione radar corrente.',
        temporalPolicy: 'live', maturity: 'operational', authority: 'observed', decisionEligible: true,
        actions: [action('control', 'chkRadar', true), action('section', 'layers', 'nowcast')],
        tags: ['radar', 'rain'], derive: radarStatus,
    },
    {
        id: 'ws.live.satellite', section: 'live', order: 40,
        title: 'Satellite MTG', description: 'Nubi osservate da EUMETSAT MTG/FCI.',
        temporalPolicy: 'live', maturity: 'operational', authority: 'observed', decisionEligible: true,
        actions: [action('control', 'chkSatellite', true), action('mode', 'intelligence', 'satellite')],
        tags: ['satellite', 'clouds'], derive: satelliteStatus,
    },
    {
        id: 'ws.live.lightning', section: 'live', order: 50,
        title: 'Fulmini', description: 'Scariche elettriche osservate.',
        temporalPolicy: 'live', maturity: 'operational', authority: 'observed', decisionEligible: true,
        actions: [action('control', 'chkLightning', true), action('section', 'layers', 'nowcast')],
        tags: ['lightning', 'storms'], derive: lightningStatus,
    },
    {
        id: 'ws.live.hail', section: 'live', order: 60,
        title: 'Grandine radar', description: 'Probabilità grandine derivata dal radar.',
        temporalPolicy: 'live', maturity: 'operational', authority: 'derived_observation', decisionEligible: true,
        actions: [action('control', 'chkHail', true), action('section', 'layers', 'nowcast')],
        tags: ['hail', 'radar'], derive: hailStatus,
    },
    {
        id: 'ws.live.storm-tracking', section: 'live', order: 70,
        title: 'Temporali e percorso', description: 'Celle temporalesche e movimento osservato.',
        temporalPolicy: 'live', maturity: 'operational', authority: 'ws_tracking', decisionEligible: true,
        actions: [action('control', 'chkTemporali', true), action('section', 'layers', 'nowcast')],
        tags: ['storms', 'tracking'], derive: stormStatus,
    },
    {
        id: 'ws.live.territorial-phenomena', section: 'live', order: 80,
        title: 'Fenomeni territoriali live', description: 'Fenomeni ricavati dalle osservazioni locali.',
        temporalPolicy: 'live', maturity: 'operational', authority: 'ws_intelligence', decisionEligible: true,
        actions: [action('control', 'wsi-insights', true), action('mode', 'intelligence', 'events')],
        tags: ['phenomena', 'events'], derive: phenomenonStatus,
    },
    {
        id: 'ws.forecast.rain-nowcast', section: 'forecast', order: 10,
        title: 'WS Now pioggia', description: 'Evoluzione radar operativa a brevissimo termine.',
        temporalPolicy: 'nowcast', maturity: 'operational', authority: 'ws_intelligence', decisionEligible: true,
        actions: [action('control', 'chkRadar', true), action('timeline', 'unified', 'nowcast')],
        tags: ['nowcast', 'radar', 'rain'], derive: nowcastStatus,
    },
    {
        id: 'ws.forecast.localities', section: 'forecast', order: 20,
        title: 'Previsioni locali', description: 'Stato previsto per le località Weather Sicily.',
        temporalPolicy: 'forecast', maturity: 'operational', authority: 'ws_forecast', decisionEligible: true,
        actions: [action('timeline', 'unified', 'forecast'), action('section', 'layers', 'fields')],
        tags: ['forecast', 'places'], derive: localForecastStatus,
    },
    {
        id: 'ws.forecast.model-fields', section: 'forecast', order: 30,
        title: 'Campi modello e WSLAM', description: 'WSLAM e modelli globali allineati nel tempo.',
        temporalPolicy: 'forecast', maturity: 'operational', authority: 'model', decisionEligible: true,
        actions: [action('control', 'chkWslam', true), action('control', 'wslam-product', 'selected')],
        tags: ['wslam', 'ecmwf', 'gfs', 'icon'], derive: modelFieldStatus,
    },
    {
        id: 'ws.forecast.atmospheric-physics', section: 'forecast', order: 40,
        title: 'Fisica atmosferica', description: 'Campi diagnostici e scenari fisici futuri.',
        temporalPolicy: 'forecast', maturity: 'beta', authority: 'model_diagnostic', decisionEligible: true,
        actions: [action('section', 'wsi-panel', 'physics'), action('timeline', 'intelligence', 'scenario')],
        tags: ['physics', 'cape', 'convection'], derive: physicsStatus,
    },
    {
        id: 'ws.tracking.synoptic-scenario', section: 'tracking', order: 10,
        title: 'Scenario sinottico', description: 'Centri A/B/C con genesi, movimento e dissoluzione.',
        temporalPolicy: 'all', maturity: 'operational', authority: 'ws_intelligence', decisionEligible: true,
        actions: [action('control', 'wsi-centers', true), action('control', 'wsi-tracks', true), action('mode', 'intelligence', 'pressure')],
        tags: ['pressure', 'centers', 'tracks'], derive: synopticStatus,
    },
    {
        id: 'ws.tracking.fronts', section: 'tracking', order: 20,
        title: 'Fronti e diagnostica sinottica', description: 'Fronti e strutture diagnostiche temporali.',
        temporalPolicy: 'all', maturity: 'beta', authority: 'model_diagnostic', decisionEligible: true,
        actions: [action('control', 'wsi-advanced', true), action('mode', 'intelligence', 'pressure')],
        tags: ['fronts', 'synoptic'], derive: frontStatus,
    },
    {
        id: 'ws.tracking.territorial-events', section: 'tracking', order: 30,
        title: 'Eventi territoriali', description: 'Eventi locali con validità ed evoluzione spaziale.',
        temporalPolicy: 'all', maturity: 'operational', authority: 'ws_intelligence', decisionEligible: true,
        actions: [action('control', 'wsi-territorial', true), action('mode', 'intelligence', 'events')],
        tags: ['events', 'territory', 'impacts'], derive: territorialStatus,
    },
    {
        id: 'ws.system.weather-state', section: 'system', order: 10,
        title: 'Weather State locale', description: 'Stato meteo coerente per ogni località.',
        temporalPolicy: 'all', maturity: 'operational', authority: 'ws_intelligence', decisionEligible: true,
        actions: [action('control', 'wsi-near-me', 'activate'), action('section', 'wsi-panel', 'local')],
        tags: ['places', 'state'], derive: weatherStateStatus,
    },
    {
        id: 'ws.system.alerts', section: 'system', order: 20,
        title: 'Allerte ed eventi', description: 'Allerte ufficiali e policy centrale WS.',
        temporalPolicy: 'all', maturity: 'operational', authority: 'official_and_ws_policy', decisionEligible: true,
        actions: [action('control', 'risk-alerts', true), action('section', 'layers', 'risks')],
        tags: ['alerts', 'risk'], derive: alertStatus,
    },
    {
        id: 'ws.system.geophysical', section: 'system', order: 30,
        title: 'Etna e geofisica', description: 'Evidenze ufficiali e strumentali geofisiche.',
        temporalPolicy: 'all', maturity: 'operational', authority: 'official_and_observed', decisionEligible: true,
        actions: [action('control', 'risk-etna', true), action('section', 'layers', 'risks')],
        tags: ['etna', 'earthquakes', 'geophysics'], derive: geophysicalStatus,
    },
    {
        id: 'ws.system.station-quality', section: 'system', order: 40,
        title: 'Qualità sensori', description: 'Quarantena e qualità dei parametri osservati.',
        temporalPolicy: 'all', maturity: 'operational', authority: 'sensor_quality_control', decisionEligible: false,
        actions: [action('section', 'wsi-panel', 'reliability'), action('section', 'admin-stations', 'quality')],
        tags: ['quality', 'stations', 'diagnostics'], derive: stationQualityStatus,
    },
    {
        id: 'ws.live.community-rain-reports', section: 'live', order: 90,
        title: 'Segnalazioni pioggia', description: 'Segnalazioni della comunità, separate dalle misure strumentali.',
        temporalPolicy: 'live', maturity: 'beta', authority: 'community_observation', decisionEligible: false,
        actions: [action('control', 'drv-rainReports', true), action('section', 'layers', 'nowcast')],
        tags: ['rain', 'reports', 'community'],
        derive: state => sourceHealthStatus(state, 'community', {
            decisionEligible: false, authority: 'community_observation',
        }),
    },
    {
        id: 'ws.live.marine-buoys', section: 'live', order: 110,
        title: 'Boe marine', description: 'Osservazioni marine e ondametriche.',
        temporalPolicy: 'live', maturity: 'beta', authority: 'observed_marine', decisionEligible: false,
        actions: [action('control', 'me-boe', true), action('section', 'layers', 'sea')],
        tags: ['marine', 'buoys', 'waves'],
        derive: state => sourceHealthStatus(state, 'marine', {
            decisionEligible: false, authority: 'observed_marine',
        }),
    },
    {
        id: 'ws.live.idrometers', section: 'live', order: 140,
        title: 'Idrometri', description: 'Livelli idrometrici osservati, con copertura dichiarata.',
        temporalPolicy: 'live', maturity: 'beta', authority: 'official_hydrology_observation', decisionEligible: false,
        actions: [action('control', 'chkIdrometri', true), action('section', 'layers', 'sea')],
        tags: ['hydro', 'rivers', 'idrometers'],
        derive: state => sourceHealthStatus(state, 'hydrometers', {
            decisionEligible: false, authority: 'official_hydrology_observation',
        }),
    },
    {
        id: 'ws.live.firms-fire-observations', section: 'live', order: 150,
        title: 'Hotspot FIRMS', description: 'Anomalie termiche satellitari: possibili incendi, non conferme automatiche.',
        temporalPolicy: 'live', maturity: 'operational', authority: 'nasa_firms_observed_hotspots', decisionEligible: false,
        actions: [action('control', 'risk-firms', true), action('section', 'view-options', 'fires')],
        tags: ['fires', 'firms', 'satellite'], derive: fireObservationStatus,
    },
    {
        id: 'ws.forecast.cams-dust', section: 'forecast', order: 50,
        title: 'CAMS e polveri sahariane', description: 'Contesto modellistico di composizione atmosferica.',
        temporalPolicy: 'forecast', maturity: 'operational', authority: 'copernicus_cams_model_context', decisionEligible: false,
        actions: [action('control', 'ovl-dust', true), action('section', 'layers', 'nowcast')],
        tags: ['cams', 'dust', 'air-quality'], derive: compositionStatus,
    },
    {
        id: 'ws.forecast.fire-weather-index', section: 'forecast', order: 80,
        title: 'Pericolo incendi FWI', description: 'Indice meteorologico di pericolo incendi, non incendio osservato.',
        temporalPolicy: 'forecast', maturity: 'operational', authority: 'fire_weather_model', decisionEligible: false,
        actions: [action('control', 'ovl-fwi', true), action('section', 'layers', 'risks')],
        tags: ['fire', 'fwi', 'forecast'],
        derive: state => sourceHealthStatus(state, 'wildfire_risk', {
            decisionEligible: false, authority: 'fire_weather_model',
        }),
    },
    {
        id: 'ws.system.earthquakes', section: 'system', order: 50,
        title: 'Terremoti INGV', description: 'Eventi osservati ufficiali; nessuna previsione deterministica.',
        temporalPolicy: 'live', maturity: 'operational', authority: 'ingv_official_observation', decisionEligible: false,
        actions: [action('control', 'drv-quakes', true), action('section', 'layers', 'risks')],
        tags: ['earthquakes', 'ingv', 'observations'], derive: earthquakeStatus,
    },
];

function temporalAllowed(policy, mode) {
    if (mode === 'all' || policy === 'all') return true;
    // The Live workspace is the discovery surface: future capabilities remain
    // selectable there. Nowcast also keeps forecast actions reachable.
    if (mode === 'live') return true;
    if (mode === 'nowcast') return ['live', 'nowcast', 'forecast'].includes(policy);
    if (mode === 'forecast') return policy === 'forecast';
    if (mode === 'history') return false;
    return false;
}

function temporalLockReason(policy, mode) {
    if (policy === 'live' && mode === 'forecast') return 'live_source_not_available_in_future';
    if (policy === 'live' && mode === 'history') return 'live_source_not_available_in_history';
    return `${policy}_product_not_available_in_${mode}`;
}

function activeControlValue(activeControls, target) {
    if (!activeControls) return undefined;
    if (activeControls instanceof Map) return activeControls.get(target);
    if (activeControls instanceof Set) return activeControls.has(target) ? true : undefined;
    if (Array.isArray(activeControls)) return activeControls.includes(target) ? true : undefined;
    if (typeof activeControls === 'object'
        && Object.prototype.hasOwnProperty.call(activeControls, target)) return activeControls[target];
    return undefined;
}

function requestedByControls(product, activeControls) {
    return product.actions
        .filter(descriptor => descriptor.type === 'control')
        .some(descriptor => {
            const activeValue = activeControlValue(activeControls, descriptor.target);
            if (activeValue === undefined) return false;
            if (descriptor.value === 'selected') return activeValue !== false && activeValue !== null && activeValue !== '';
            return activeValue === descriptor.value;
        });
}

function safeDerivedState(derive, state) {
    try {
        const derived = derive(state || {});
        return derived && typeof derived === 'object'
            ? derived
            : result('unknown', {}, {reason: 'invalid_state_resolver_result'});
    } catch (error) {
        return result('error', {}, {
            available: false,
            reason: `state_resolver_failed:${error?.message || 'unknown'}`,
        });
    }
}

function productSnapshot(entry, state, mode, activeControls) {
    const product = entry.publicDefinition;
    const derived = safeDerivedState(entry.derive, state);
    const allowed = temporalAllowed(product.temporalPolicy, mode);
    const requestedActive = requestedByControls(product, activeControls);
    const sourceAvailable = derived.available !== false;
    const available = allowed && sourceAvailable;
    const sourceStatus = String(derived.status || 'unknown');
    const status = allowed ? sourceStatus : 'time_locked';
    const governance = {
        maturity: derived.maturity || product.governance.maturity,
        authority: derived.authority || product.governance.authority,
        decisionEligible: derived.decisionEligible ?? product.governance.decisionEligible,
    };
    const effectiveDecisionEligible = available && governance.decisionEligible === true;
    return deepFreeze({
        ...product,
        governance: deepFreeze(governance),
        mode,
        status,
        sourceStatus,
        available,
        active: requestedActive && available,
        requestedActive,
        decisionEligible: effectiveDecisionEligible,
        temporal: {
            policy: product.temporalPolicy,
            mode,
            allowed,
            locked: !allowed,
            reason: allowed ? null : temporalLockReason(product.temporalPolicy, mode),
        },
        reason: allowed ? (derived.reason ?? null) : temporalLockReason(product.temporalPolicy, mode),
        coverage: derived.coverage ?? null,
        metrics: derived.metrics && typeof derived.metrics === 'object' ? {...derived.metrics} : {},
    });
}

function compareProducts(a, b) {
    const sectionDelta = SECTION_BY_ID.get(a.publicDefinition.section).order
        - SECTION_BY_ID.get(b.publicDefinition.section).order;
    return sectionDelta
        || a.publicDefinition.order - b.publicDefinition.order
        || a.publicDefinition.id.localeCompare(b.publicDefinition.id);
}

const BUILTIN_ENTRIES = BUILTIN_DEFINITIONS.map(definition => ({
    publicDefinition: deepFreeze({
        id: definition.id,
        section: definition.section,
        order: definition.order,
        title: definition.title,
        description: definition.description,
        temporalPolicy: definition.temporalPolicy,
        governance: {
            maturity: definition.maturity,
            authority: definition.authority,
            decisionEligible: definition.decisionEligible,
        },
        actions: definition.actions,
        tags: definition.tags,
    }),
    derive: definition.derive,
})).sort(compareProducts);

export const BUILTIN_PLATFORM_PRODUCTS = deepFreeze(
    BUILTIN_ENTRIES.map(entry => entry.publicDefinition),
);

export function listPlatformProducts() {
    return BUILTIN_PLATFORM_PRODUCTS;
}

export function buildPlatformSnapshot(state = {}, options = {}) {
    const mode = options.mode || 'live';
    if (!TEMPORAL_MODES.has(mode)) throw new TypeError(`Invalid platform snapshot mode: ${mode}`);
    const products = BUILTIN_ENTRIES
        .map(entry => productSnapshot(entry, state, mode, options.activeControls));
    const sections = Object.values(PLATFORM_SECTIONS)
        .sort((a, b) => a.order - b.order)
        .map(section => {
            const sectionProducts = products.filter(product => product.section === section.id);
            return deepFreeze({
                ...section,
                productCount: sectionProducts.length,
                availableCount: sectionProducts.filter(product => product.available).length,
                activeCount: sectionProducts.filter(product => product.active).length,
                lockedCount: sectionProducts.filter(product => product.temporal.locked).length,
            });
        });
    return deepFreeze({
        schemaVersion: 1,
        generatedAtUtc: state?.generated_at_utc ?? null,
        mode,
        sections,
        products,
        summary: {
            productCount: products.length,
            availableCount: products.filter(product => product.available).length,
            activeCount: products.filter(product => product.active).length,
            lockedCount: products.filter(product => product.temporal.locked).length,
            degradedCount: products.filter(product => ['degraded', 'error'].includes(product.status)).length,
        },
    });
}
